import { formatValue } from "../utils/helpers";
import metricMetadataIcon from '../assets/metric-metadata-icon-blue.svg';
import { CSSProperties, useEffect, useMemo, useRef, useState } from "react";
import MetricMetadataModal from "./MetricMetadataModal";
import { DisaggregatedMetric, DistributionData, GeoDetails, GeoTag, MetricMetadata, RawIndicatorData } from "../services/types";
import { fetchDistributionData, fetchIndicatorBinCount, fetchMetricMetadata, indexMetricIds } from "../utils/dashboardUtils";
import { useDashboardColor } from "../context/DashboardColorContext";
import DefaultIcon from '../assets/svgr/port/default-metric.svg?react'
import { useCountyIndicators } from "../context/CountyIndicatorsContext";

interface MetricCardProps {
    metric: DisaggregatedMetric;
    width: string;
    disaggData: DisaggregatedMetric[] | null;
    geoDetails: GeoDetails[];
    geoTag: GeoTag | null;
    selectionActive: boolean;
    svg?: React.FC<React.SVGProps<SVGSVGElement>>;
    showMetadataIcon?: boolean;
};

const MetricCard = ({ metric, disaggData, geoDetails, geoTag, selectionActive, svg: SvgIcon, showMetadataIcon = true }: MetricCardProps) => {
    const { data: countyIndicators } = useCountyIndicators();
    const color = useDashboardColor();
    const IconComponent = SvgIcon || DefaultIcon;

    const [showMetadata, setShowMetadata] = useState(false);
    const [metadata, setMetadata] = useState<MetricMetadata[] | null>(null);
    const [distributionData, setDistributionData] = useState<RawIndicatorData[] | null>(null);
    const [countyDistributionData, setCountyDistributionData] = useState<DistributionData[] | null>(null);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    // Destructure metric properties
    const { id, name, versions } = metric;
    const { value, unit, unit_descriptor, percent_calc } = versions[0];

    const val = unit === "ratio" ? `${percent_calc?.n}/${percent_calc?.d}` : value;

    // Index metric?
    const isIndexMetric = indexMetricIds.includes(metric.id);

    // Find county data for metric
    const countyMetric = useMemo(() => {
        return countyIndicators?.find((indicator) => indicator.id === id) ?? null;
    }, [countyIndicators, id]);

    const countyVersion = countyMetric?.versions?.[0];

    const countyRawValue =
        countyVersion?.unit === "ratio"
            ? `${countyVersion?.percent_calc?.n}/${countyVersion?.percent_calc?.d}`
            : countyVersion?.value;

    const countyDisplayValue =
        countyVersion && countyRawValue !== undefined && countyRawValue !== null
            ? formatValue(countyRawValue, countyVersion.unit, countyMetric.id)
            : null;

    // Refs for metadata modal
    const triggerRef = useRef<HTMLButtonElement | null>(null);
    const modalId = `metric-metadata-modal-${id ?? 'main'}`;
    const clickDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const fetchCache = useRef<Record<string, Promise<any> | undefined>>({});

    function fetchWithCache<T>(key: string, fetchFn: () => Promise<T>): Promise<T> {
        if (fetchCache.current[key]) {
            // Return ongoing request if already in-flight
            return fetchCache.current[key] as Promise<T>;
        }

        const promise = fetchFn().finally(() => {
            delete fetchCache.current[key];
        });

        fetchCache.current[key] = promise;
        return promise;
    }

    // Prefetch on mount (throttled sequentially)
    useEffect(() => {
        // Only fetch if not already cached or loaded
        if (!metadata) {
            fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id))
            .then(res => res?.data && setMetadata(res.data))
            .catch(console.error);
        }

        if (!distributionData) {
            const distKey = `distData-${id}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`;
            fetchWithCache(distKey, () => fetchIndicatorBinCount(id, geoDetails, geoTag))
            .then(res => res && setDistributionData(res))
            .catch(console.error);
        }

        if (!countyDistributionData) {
            const countyKey = `countyDist-${id}-53033`;
            fetchWithCache(countyKey, () => fetchDistributionData(id, [], null))
            .then(res => res && setCountyDistributionData(res))
            .catch(console.error);
        }
    }, [id, geoDetails, geoTag]);

    const handleClick = async () => {
        if (clickDebounceRef.current) clearTimeout(clickDebounceRef.current);

        clickDebounceRef.current = setTimeout(async () => {
            setShowMetadata((prev) => !prev);
            setLoading(true);

            try {
            const [metadataResponse, distributionDataResponse, countyDistributionDataResponse] = await Promise.all([
                metadata
                ? Promise.resolve({ data: metadata })
                : fetchWithCache(`metadata-${id}`, () => fetchMetricMetadata(id)),

                distributionData
                ? Promise.resolve(distributionData)
                : fetchWithCache(`distData-${id}-${geoDetails.map(d => d.lod).join(',')}-${geoTag?.type}-${geoTag?.value}`, () =>
                    fetchIndicatorBinCount(id, geoDetails, geoTag)
                    ),

                countyDistributionData
                ? Promise.resolve(countyDistributionData)
                : fetchWithCache(`countyDist-${id}-53033`, () =>
                    fetchDistributionData(id, [], null)
                    ),
            ]);

            if (!metadata) setMetadata(metadataResponse?.data || null);
            if (!distributionData) setDistributionData(distributionDataResponse || null);
            if (!countyDistributionData) setCountyDistributionData(countyDistributionDataResponse || null);

            } catch (error) {
            const errorMessage = (error as Error).message || 'An unexpected error occurred.';
            console.error('Error fetching data:', error);
            setError(new Error(`Failed to load data: ${errorMessage}`));
            } finally {
            setLoading(false);
            }
        }, 150);
    };

    return (
        <div 
            className="relative overflow-hidden group hover:shadow-lg transition-all border rounded-xl bg-white" 
            style={{ 
                borderLeftWidth: isIndexMetric ? "4px" : undefined, borderLeftColor: isIndexMetric ? color : undefined,
                }}
        >
            <div className="p-4">
                <div className="flex items-start justify-between mb-1">
                    <div className="flex items-center space-x-2">
                        {/* Metric Icon (dynamic colors) */}
                        {SvgIcon && (
                            <div
                                className="group rounded-lg p-2 transition-colors bg-[var(--bg)] group-hover:bg-[var(--bg-hover)]"
                                style={
                                    {
                                    "--bg": `${color}20`,
                                    "--bg-hover": `${color}40`,
                                    } as CSSProperties
                                }
                            >
                                <IconComponent
                                    className="w-8 h-8"
                                    style={{ color }}
                                    aria-label={`${name} icon`}
                                    role="img"
                                />
                            </div>
                        )}
                        {/* Metric Value */}
                        <span className="text-3xl font-semibold tracking-tight">
                            {formatValue(val, unit, id)}
                        </span>
                    </div>

                    {/* Metadata Icon */}
                    {showMetadataIcon && 
                        <button
                            type="button"
                            onClick={handleClick}
                            aria-haspopup="dialog"
                            aria-expanded={showMetadata ? 'true' : 'false'}
                            aria-controls={modalId}
                            aria-label={`Additional information about ${name}`}
                            className="
                            inline-flex h-6 w-6 items-center justify-center rounded
                            transition-transform hover:scale-105 focus:scale-105
                            focus-visible:outline-none
                            focus-visible:ring-2 focus-visible:ring-offset-2
                            focus-visible:ring-neutral-900 focus-visible:ring-offset-white
                            dark:focus-visible:ring-white dark:focus-visible:ring-offset-neutral-900
                            "
                        >
                            <img src={metricMetadataIcon} alt="" aria-hidden="true" className="h-3 w-3" draggable="false" />
                        </button>
                    }
                </div>

                <div className="space-y-0.5">
                    <h3 className="text-sm font-semibold text-port-text">
                        {name}
                    </h3>
                    <p className="text-xs text-muted-foreground leading-tight">
                        {unit_descriptor}
                    </p>
                    {isIndexMetric &&
                        <div className="rounded-lg h-4 w-10 px-1.5 text-[10px] text-white text-center" style={{ backgroundColor: color }}>Index</div> 
                    }
                    {countyDisplayValue && (
                        <div
                            className="
                                mt-1 text-xs font-medium text-port-text/80
                                opacity-0 max-h-0 overflow-hidden
                                translate-y-1
                                transition-all duration-200
                                group-hover:opacity-100 group-hover:max-h-8 group-hover:translate-y-0
                            "
                        >
                            King County: <span className="font-semibold">{countyDisplayValue}</span>
                        </div>
                    )}

                </div>
            </div>
            <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#2258e5]/50 to-[#00ac4e]/50 opacity-0 group-hover:opacity-100 transition-opacity" />

            <MetricMetadataModal
                isOpen={showMetadata}
                onClose={handleClick}
                id={id}
                metadata={metadata}
                stateData={null}
                nationalData={null}
                disaggData={disaggData}
                distributionData={distributionData}
                countyDistributionData={countyDistributionData}
                selectionActive={selectionActive}
                // svg={svg}
                loading={loading}
                error={error}
                triggerRef={triggerRef}
                modalId={modalId}
            />
        </div>
    );
};


export default MetricCard;
